import { useState, useEffect, useRef, useCallback } from 'react';
import { Search, Building2, Loader2, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { companiesHouseApi } from '@/services/api';
import { toast } from 'sonner';

function getSearchErrorMessage(err) {
  const message = err?.message?.trim();
  if (!message) return 'Failed to search. Please try again.';
  if (/failed to fetch|networkerror|network request failed/i.test(message)) {
    return 'Cannot reach the backend API. Check that the iXBRL backend is running and reachable.';
  }
  return message;
}

// Optional props for use outside the signed-in app (sign-up form): `searchFn(query)` replaces the
// authenticated search, `fetchProfile={false}` skips the authenticated profile call, `onQueryChange`
// reports what is typed (so free text is still accepted), `compact` uses the normal input height.
const CompanySearch = ({
  onSelect,
  onManualEntry,
  placeholder = "Type your company name or registration number...",
  searchFn,
  fetchProfile = true,
  onQueryChange,
  minChars = 2,
  compact = false,
  inputId,
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  const searchCompanies = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.length < minChars) {
      setResults([]);
      setError(null);
      setIsOpen(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = searchFn ? await searchFn(searchQuery) : await companiesHouseApi.search(searchQuery, 8);
      setResults(data.items || []);
      setIsOpen((data.items || []).length > 0);
      setSelectedIndex(-1);
    } catch (err) {
      setError(getSearchErrorMessage(err));
      setResults([]);
      setIsOpen(true);
    } finally {
      setLoading(false);
    }
  }, [searchFn, minChars]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    if (onQueryChange) onQueryChange(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchCompanies(value), 300);
  };

  const handleSelect = async (company) => {
    setQuery(company.company_name);
    setIsOpen(false);
    setResults([]);
    if (!fetchProfile) {
      onSelect(company);
      return;
    }
    try {
      setLoading(true);
      const profile = await companiesHouseApi.getProfile(company.company_number);
      onSelect(profile);
    } catch (err) {
      toast.warning('Could not fetch full company details. Some fields may be incomplete.');
      onSelect(company);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (!isOpen || results.length === 0) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, results.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0) handleSelect(results[selectedIndex]);
        break;
      case 'Escape':
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => () => debounceRef.current && clearTimeout(debounceRef.current), []);

  const getStatusBadge = (status) => {
    const config = {
      active: { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50' },
      dissolved: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-50' },
      liquidation: { icon: AlertCircle, color: 'text-amber-600', bg: 'bg-amber-50' },
    }[status] || { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50' };
    const Icon = config.icon;
    return (
      <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium', config.bg, config.color)}>
        <Icon className="w-3 h-3" />
        {status}
      </span>
    );
  };

  return (
    <div ref={wrapperRef} className="relative w-full">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
        <Input
          ref={inputRef}
          id={inputId}
          type="text"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => results.length > 0 && setIsOpen(true)}
          placeholder={placeholder}
          className={cn('pl-10 pr-10', compact ? '' : 'h-12 text-base')}
          autoComplete="off"
          data-testid="company-search-input"
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground animate-spin" />
        )}
      </div>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-lg max-h-80 overflow-y-auto">
          {error ? (
            <div className="p-4 text-center text-sm">
              <AlertCircle className="w-5 h-5 mx-auto mb-2 text-red-600" />
              <p className="text-red-600">{error}</p>
              {onManualEntry && (
                <button
                  type="button"
                  onClick={() => { setIsOpen(false); onManualEntry(); }}
                  className="mt-2 text-primary underline hover:text-primary/80"
                >
                  Enter company details manually
                </button>
              )}
            </div>
          ) : results.length === 0 ? (
            <div className="p-4 text-center text-muted-foreground text-sm">No companies found</div>
          ) : (
            <ul className="py-1">
              {results.map((company, index) => (
                <li
                  key={company.company_number}
                  className={cn(
                    'px-4 py-3 cursor-pointer transition-colors border-b border-border last:border-b-0',
                    selectedIndex === index ? 'bg-primary/10' : 'hover:bg-muted/50'
                  )}
                  onClick={() => handleSelect(company)}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center shrink-0 mt-0.5">
                      <Building2 className="w-4 h-4 text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <p className="font-semibold text-sm truncate">{company.company_name}</p>
                        {company.company_status && getStatusBadge(company.company_status)}
                      </div>
                      <p className="text-xs text-muted-foreground font-mono">{company.company_number}</p>
                      {company.address_snippet && (
                        <p className="text-xs text-muted-foreground truncate mt-0.5">{company.address_snippet}</p>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default CompanySearch;
